import React from 'react';

/**
 * Lightweight, dependency-free markdown renderer for AI chat replies.
 * Renders into React elements (no dangerouslySetInnerHTML), so untrusted text
 * cannot inject HTML. Supports the subset the assistant actually produces:
 * headings, ordered/unordered lists, bold, italic, inline code, and links.
 */

// Allow only safe link schemes; returns null for anything else.
function sanitizeUrl(raw: string): string | null {
  const url = raw.trim();
  if (/^(https?:|mailto:)/i.test(url)) return url;
  return null;
}

// Inline tokens: `code`, **bold**, *italic*, _italic_, [label](url).
const INLINE_RE = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(_[^_\n]+_)|(\[[^\]]+\]\([^)]+\))/g;

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyPrefix}-${k++}`;
    if (tok.startsWith('`')) {
      nodes.push(
        <code
          key={key}
          className="rounded bg-black/5 dark:bg-white/10 px-1 py-0.5 text-[0.85em] font-mono"
        >
          {tok.slice(1, -1)}
        </code>
      );
    } else if (tok.startsWith('**')) {
      nodes.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('*')) {
      nodes.push(<em key={key}>{tok.slice(1, -1)}</em>);
    } else if (tok.startsWith('_')) {
      nodes.push(<em key={key}>{tok.slice(1, -1)}</em>);
    } else {
      const lm = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      if (lm) {
        const href = sanitizeUrl(lm[2]);
        nodes.push(
          href ? (
            <a
              key={key}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 dark:text-primary-300 underline underline-offset-2"
            >
              {lm[1]}
            </a>
          ) : (
            lm[1]
          )
        );
      } else {
        nodes.push(tok);
      }
    }
    last = INLINE_RE.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

const UL_RE = /^\s*[-*•]\s+(.*)$/;
const OL_RE = /^\s*(\d+)[.)]\s+(.*)$/;
const H_RE = /^(#{1,6})\s+(.*)$/;

const MarkdownMessage: React.FC<{ content: string; className?: string }> = ({ content, className }) => {
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line — skip (block separation).
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Heading
    const h = H_RE.exec(line);
    if (h) {
      const level = Math.min(h[1].length, 6);
      const sizeClass =
        level <= 1 ? 'text-base font-semibold' : level === 2 ? 'text-sm font-semibold' : 'text-sm font-medium';
      blocks.push(
        <p key={`b${key++}`} className={`${sizeClass} mt-1`}>
          {renderInline(h[2], `h${key}`)}
        </p>
      );
      i++;
      continue;
    }

    // Unordered list
    if (UL_RE.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && UL_RE.test(lines[i])) {
        const mm = UL_RE.exec(lines[i]) as RegExpExecArray;
        items.push(<li key={`li${key}-${items.length}`}>{renderInline(mm[1], `ul${key}-${items.length}`)}</li>);
        i++;
      }
      blocks.push(
        <ul key={`b${key++}`} className="list-disc pl-5 space-y-1">
          {items}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (OL_RE.test(line)) {
      const items: React.ReactNode[] = [];
      const startMatch = OL_RE.exec(line) as RegExpExecArray;
      const start = Number(startMatch[1]) || 1;
      while (i < lines.length && OL_RE.test(lines[i])) {
        const mm = OL_RE.exec(lines[i]) as RegExpExecArray;
        items.push(<li key={`oli${key}-${items.length}`}>{renderInline(mm[2], `ol${key}-${items.length}`)}</li>);
        i++;
      }
      blocks.push(
        <ol key={`b${key++}`} start={start} className="list-decimal pl-5 space-y-1">
          {items}
        </ol>
      );
      continue;
    }

    // Paragraph — merge consecutive non-blank, non-structural lines with <br/>.
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !H_RE.test(lines[i]) &&
      !UL_RE.test(lines[i]) &&
      !OL_RE.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={`b${key++}`} className="leading-relaxed">
        {paraLines.map((pl, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <br />}
            {renderInline(pl, `p${key}-${idx}`)}
          </React.Fragment>
        ))}
      </p>
    );
  }

  return <div className={`space-y-2 ${className ?? ''}`}>{blocks}</div>;
};

export default MarkdownMessage;
